import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export const useGamification = () => {
 const { user, profile, refreshProfile } = useAuth();
 const [rank, setRank] = useState<number | null>(null);
 const [totalParticipants, setTotalParticipants] = useState(0);

 const fetchRank = async () => {
    if (!user) return;
    try {
      // Get total players for ranking context
      const { count } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .eq("is_player", true);
        
      setTotalParticipants(count || 0);

      // Get position based on total_points
      const { data: rankData } = await supabase
        .from("profiles")
        .select("user_id, total_points, full_name")
        .eq("is_player", true)
        .order("total_points", { ascending: false });

      if (rankData) {
        // Excluir administradores ou o próprio Hudson Raone da listagem para não bugar o ranking
        const filteredRank = rankData.filter(p => 
          !p.full_name?.toLowerCase().includes("hudson raone")
        );
        
        const position = filteredRank.findIndex(p => p.user_id === user.id) + 1;
        
        // Se a posição for 0 significa que ele foi filtrado e não está no ranking
        if (position > 0) {
          if (rank !== null && position < rank) {
            toast.success("Você subiu no ranking! 🔥", {
              description: `Nova posição: ${position}º`,
              icon: "🚀",
            });
            broadcastRankUp(profile?.full_name || "Alguém", position);
          }
          setRank(position);
        } else {
          setRank(null);
        }
      }
    } catch (err) {
      console.warn("[Gamification] fetchRank error (non-fatal):", err);
    }
  };

 useEffect(() => {
 fetchRank();

 // Listen to profile updates (points change)
 const channelName = `profile_points_${Math.random().toString(36).substring(7)}`;
 const channel = supabase
 .channel(channelName)
 .on(
 "postgres_changes",
 {
 event: "UPDATE",
 schema: "public",
 table: "profiles",
 filter: `user_id=eq.${user?.id}`,
 },
 () => {
 refreshProfile();
 fetchRank();
 }
 )
 .subscribe();

 return () => {
 supabase.removeChannel(channel);
 };
 }, [user]);

 const broadcastRankUp = async (name: string, newPos: number) => {
 if (!user) return;
 try {
 // For now, let's notify the top 20 players about this change to avoid massive DB impact
 // Later this can be moved to an Edge Function for true full broadcast
 const { data: topPlayers } = await supabase
 .from("profiles")
 .select("user_id")
 .eq("is_player", true)
 .neq("user_id", user.id)
 .limit(20);

 if (topPlayers && topPlayers.length > 0) {
 const notifications = topPlayers.map(p => ({
 user_id: p.user_id,
 message: `${name} acabou de subir para a ${newPos}ª posição no ranking! 🔥 A disputa está ficando quente!`,
 type: "rank_up"
 }));
 await (supabase.from("notifications" as any) as any).insert(notifications);
 }
 } catch (err) {
 console.warn("[Gamification] broadcastRankUp error:", err);
 }
 };

 const awardPoints = async (amount: number, reason: string) => {
 if (!user) return;
 try {
 const { error } = await supabase.from("points_logs").insert({
 user_id: user.id,
 amount,
 reason,
 });
 if (error) console.warn("[Gamification] awardPoints error:", error.message);
 return { error };
 } catch (err) {
 console.warn("[Gamification] awardPoints exception (non-fatal):", err);
 return { error: err };
 }
 };

 const getExperienceProgress = () => {
 if (!profile) return 0;
 const points = profile.points || 0;
 // Simple level logic: 1000 points per level
 const progress = (points % 1000) / 10;
 return progress;
 };

 return {
 rank,
 totalParticipants,
 awardPoints,
 getExperienceProgress,
 points: profile?.points || 0,
 level: profile?.level || 1,
 };
};
